import { Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GfFlyerReviewChangeRequest } from "@swagger/typescript-fetch-goflyer";
import {
  CR_STATUS_COLORS,
  CR_STATUS_LABEL_FACTORIES,
} from "../statusColors";
import { reviewTokens } from "../../../theme";

const St = GfFlyerReviewChangeRequest.StatusEnum;

const STATUS_DOT_COLOR: Record<GfFlyerReviewChangeRequest.StatusEnum, string> =
  {
    [St.PENDING]: reviewTokens.info,
    [St.ACCEPTED]: reviewTokens.success,
    [St.REJECTED]: reviewTokens.danger,
    [St.SUPERSEDED]: reviewTokens.warning,
    [St.RESTORED]: reviewTokens.slate400,
  };

/** Status-dot color for a change-request status (used by the history rail). */
export const statusDotColor = (
  status: GfFlyerReviewChangeRequest.StatusEnum,
): string => STATUS_DOT_COLOR[status];

export interface CrStatusChipProps {
  status: GfFlyerReviewChangeRequest.StatusEnum;
  /**
   * `chip` = MUI semantic color, filled when pending (change-request row).
   * `dot`  = status-dot tinted background (history timeline node).
   */
  tone: "chip" | "dot";
}

/**
 * Status chip for a change request. Centralizes the label resolution that was
 * duplicated in `ChangeRequestRow` and `HistoryTree`; `tone` reproduces each
 * site's original styling exactly.
 */
export function CrStatusChip({ status, tone }: CrStatusChipProps) {
  const { t } = useTranslation();
  const label = CR_STATUS_LABEL_FACTORIES[status]
    ? t(CR_STATUS_LABEL_FACTORIES[status]())
    : status;

  if (tone === "dot") {
    const dot = STATUS_DOT_COLOR[status];
    return (
      <Chip
        size="small"
        label={label}
        sx={{
          bgcolor: `${dot}1F`,
          color: dot,
          borderColor: `${dot}66`,
          border: `1px solid`,
          fontWeight: 600,
          letterSpacing: "0.04em",
        }}
      />
    );
  }

  return (
    <Chip
      size="small"
      label={label}
      color={CR_STATUS_COLORS[status] || "default"}
      variant={status === St.PENDING ? "filled" : "outlined"}
      sx={{ fontWeight: 600, letterSpacing: "0.04em" }}
    />
  );
}
